import { clamp, isMobile } from "../utils";

export class Contest {
  tabControlList: HTMLUListElement;
  tabControlHighlightList: HTMLUListElement;
  tabControlListItems: HTMLLIElement[];
  activeTabControlListItem: HTMLLIElement;

  tabBody: HTMLDivElement;
  tabBodyItems: HTMLDivElement[];
  activeTabBodyItem: HTMLDivElement;

  activeTabIndex: number;

  countdown: HTMLDivElement;
  contestTimestamps: number[];

  forceAnimateCountdown = false;

  bodyResizeObserver: ResizeObserver;

  constructor(contestTimestamps: number[]) {
    this.contestTimestamps = contestTimestamps;

    this.tabControlList = document.querySelector(
      "[data-contest-tab-control-list]"
    ) as HTMLUListElement;

    const tabControlHighlight = document.querySelector(
      "[data-contest-tab-control-highlight]"
    ) as HTMLDivElement;

    this.tabControlHighlightList = this.tabControlList.cloneNode(
      true
    ) as HTMLUListElement;

    this.tabControlHighlightList.querySelectorAll("li").forEach((li) => {
      li.classList.remove("homepage-contest-nav__item--past");
      li.classList.remove("homepage-contest-nav__item--active");
    });

    tabControlHighlight.appendChild(this.tabControlHighlightList);

    this.tabControlListItems = Array.from(
      this.tabControlList.querySelectorAll("li")
    );

    this.activeTabControlListItem = this.tabControlList.querySelector(
      ".homepage-contest-nav__item--active"
    ) as HTMLLIElement;

    this.activeTabIndex = Array.from(this.tabControlListItems).indexOf(
      this.activeTabControlListItem
    );

    this.tabBody = document.querySelector(
      "#contest-tab-body"
    ) as HTMLDivElement;

    this.tabBodyItems = Array.from(
      this.tabBody.querySelectorAll(".homepage-contest-details")
    );

    this.activeTabBodyItem = this.tabBody.querySelector(
      ".homepage-contest-details--active"
    ) as HTMLDivElement;

    this.countdown = document.querySelector(
      "#homepage-contest-countdown"
    ) as HTMLDivElement;

    this.bodyResizeObserver = new ResizeObserver(this.onBodyResize);
    this.bodyResizeObserver.observe(document.body);

    this.init();
  }

  init() {
    this.updateTabControlListPosition();

    this.tabControlListItems.forEach((item, index) => {
      const tabButton = item.querySelector("button") as HTMLButtonElement;

      tabButton.addEventListener("click", () => this.onTabClick(index));
    });

    this.tabBodyItems.forEach((item, index) => {
      item.setAttribute(
        "aria-hidden",
        index !== this.activeTabIndex ? "true" : "false"
      );
    });

    this.initializeCountdown();

    if (isMobile()) {
      const parent = this.tabControlList.parentElement as HTMLElement;

      parent.style.setProperty("touch-action", "none");

      this.tabControlList.parentElement?.addEventListener(
        "pointerdown",
        this.onNavPointerDown
      );
    }
  }

  onNavPointerDown = (e: PointerEvent) => {
    if (e.pointerType !== "touch") {
      return;
    }

    e.preventDefault();

    const startX = e.clientX;

    const tabControlListX = this.tabControlList.getBoundingClientRect().x;

    const { x: activeTabControlX, width: activeTabControlWidth } =
      this.activeTabControlListItem.getBoundingClientRect();

    const startTranslateX =
      -activeTabControlX -
      activeTabControlWidth / 2 +
      document.body.clientWidth / 2 +
      tabControlListX;

    const startActiveIndex = this.activeTabIndex;

    this.tabControlList.style.setProperty("transition", "none");
    this.tabControlHighlightList.style.setProperty("transition", "none");

    const onWindowPointerMove = (e: PointerEvent) => {
      const deltaX = e.clientX - startX;
      const translateX = startTranslateX + deltaX;

      this.tabControlList.style.setProperty(
        "transform",
        `translateX(${translateX}px)`
      );

      this.tabControlHighlightList.style.setProperty(
        "transform",
        `translateX(${translateX}px)`
      );

      const nextActiveIndex = Math.round(
        startActiveIndex - deltaX / activeTabControlWidth
      );

      const index = clamp(
        nextActiveIndex,
        0,
        this.tabControlListItems.length - 1
      );

      this.onTabClick(index, false, true);
    };

    const onWindowPointerUp = (e: PointerEvent) => {
      const deltaX = e.clientX - startX;

      const nextActiveIndex =
        startActiveIndex - Math.round(deltaX / activeTabControlWidth);

      const index = clamp(
        nextActiveIndex,
        0,
        this.tabControlListItems.length - 1
      );

      this.tabControlList.style.removeProperty("transition");
      this.tabControlHighlightList.style.removeProperty("transition");

      this.onTabClick(index, true);

      window.removeEventListener("pointermove", onWindowPointerMove);
      window.removeEventListener("pointerup", onWindowPointerUp);
    };

    window.addEventListener("pointermove", onWindowPointerMove);
    window.addEventListener("pointerup", onWindowPointerUp);
  };

  updateTabControlListPosition = () => {
    const tabControlListX = this.tabControlList.getBoundingClientRect().x;

    const { x: activeTabControlX, width: activeTabControlWidth } =
      this.activeTabControlListItem.getBoundingClientRect();

    const translateX =
      -activeTabControlX -
      activeTabControlWidth / 2 +
      document.body.clientWidth / 2 +
      tabControlListX;

    this.tabControlList.style.setProperty(
      "transform",
      `translateX(${translateX}px)`
    );

    this.tabControlHighlightList.style.setProperty(
      "transform",
      `translateX(${translateX}px)`
    );
  };

  onTabClick = (
    tabIndex: number,
    forceUpdate?: boolean,
    isDragging?: boolean
  ) => {
    if (!forceUpdate && tabIndex === this.activeTabIndex) {
      return;
    }

    if (tabIndex !== this.activeTabIndex) {
      this.forceAnimateCountdown = true;
    }

    this.activeTabIndex = tabIndex;

    this.activeTabControlListItem.classList.remove(
      "homepage-contest-nav__item--active"
    );

    this.activeTabBodyItem.classList.remove("homepage-contest-details--active");
    this.activeTabBodyItem.setAttribute("aria-hidden", "true");

    const newActiveTabControlListItem = this.tabControlListItems[tabIndex];
    const newActiveTabBodyItem = this.tabBodyItems[tabIndex];

    newActiveTabControlListItem.classList.add(
      "homepage-contest-nav__item--active"
    );

    newActiveTabBodyItem.classList.add("homepage-contest-details--active");
    newActiveTabBodyItem.setAttribute("aria-hidden", "false");

    this.activeTabControlListItem = newActiveTabControlListItem;
    this.activeTabBodyItem = newActiveTabBodyItem;

    if (!isDragging) {
      this.updateTabControlListPosition();
    }
  };

  initializeCountdown = () => {
    if (!this.countdown) {
      return;
    }

    const daysEl = this.countdown.querySelector("#countdown-days");
    const hoursEl = this.countdown.querySelector("#countdown-hours");
    const minutesEl = this.countdown.querySelector("#countdown-minutes");
    const secondsEl = this.countdown.querySelector("#countdown-seconds");

    if (!daysEl || !hoursEl || !minutesEl || !secondsEl) {
      return;
    }

    const updateCountdown = () => {
      const now = Date.now();
      const targetDateMilliseconds =
        this.contestTimestamps[this.activeTabIndex] * 1000;
      const diff = targetDateMilliseconds - now;

      if (!daysEl.innerHTML) {
        daysEl.innerHTML = `<span aria-hidden="true">00</span><span>00</span>`;
        hoursEl.innerHTML = `<span aria-hidden="true">00</span><span>00</span>`;
        minutesEl.innerHTML = `<span aria-hidden="true">00</span><span>00</span>`;
        secondsEl.innerHTML = `<span aria-hidden="true">00</span><span>00</span>`;

        requestAnimationFrame(updateCountdown);
        return;
      }

      const isPast = diff < 0;

      const isPrevPast = daysEl.children[1].classList.contains(
        "homepage-contest-countdown-digits-past"
      );

      this.countdown.classList.toggle(
        "homepage-contest-countdown--past",
        isPast
      );

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      const nextDays = String(Math.max(days, 0)).padStart(2, "0");
      const nextHours = String(Math.max(hours, 0)).padStart(2, "0");
      const nextMinutes = String(Math.max(minutes, 0)).padStart(2, "0");
      const nextSeconds = String(Math.max(seconds, 0)).padStart(2, "0");

      const forceUpdate =
        isPast !== isPrevPast || (this.forceAnimateCountdown && !isPast);

      if (daysEl.children[1].textContent !== nextDays || forceUpdate) {
        daysEl.classList.add("homepage-contest-countdown-digits--updating");

        daysEl.children[0].innerHTML = daysEl.children[1].innerHTML;

        daysEl.children[0].classList.toggle(
          "homepage-contest-countdown-digits-past",
          isPrevPast
        );

        daysEl.children[1].innerHTML = nextDays;

        daysEl.children[1].classList.toggle(
          "homepage-contest-countdown-digits-past",
          isPast
        );

        const valueEl = daysEl.parentElement as HTMLSpanElement;

        valueEl.classList.toggle(
          "homepage-contest-countdown-value--three-digits",
          nextDays.length === 3
        );
      } else {
        daysEl.classList.remove("homepage-contest-countdown-digits--updating");
      }

      if (hoursEl.children[1].textContent !== nextHours || forceUpdate) {
        hoursEl.classList.add("homepage-contest-countdown-digits--updating");

        hoursEl.children[0].innerHTML = hoursEl.children[1].innerHTML;

        hoursEl.children[0].classList.toggle(
          "homepage-contest-countdown-digits-past",
          isPrevPast
        );

        hoursEl.children[1].innerHTML = nextHours;

        hoursEl.children[1].classList.toggle(
          "homepage-contest-countdown-digits-past",
          isPast
        );
      } else {
        hoursEl.classList.remove("homepage-contest-countdown-digits--updating");
      }

      if (minutesEl.children[1].textContent !== nextMinutes || forceUpdate) {
        minutesEl.classList.add("homepage-contest-countdown-digits--updating");

        minutesEl.children[0].innerHTML = minutesEl.children[1].innerHTML;

        minutesEl.children[0].classList.toggle(
          "homepage-contest-countdown-digits-past",
          isPrevPast
        );

        minutesEl.children[1].innerHTML = nextMinutes;

        minutesEl.children[1].classList.toggle(
          "homepage-contest-countdown-digits-past",
          isPast
        );
      } else {
        minutesEl.classList.remove(
          "homepage-contest-countdown-digits--updating"
        );
      }

      if (secondsEl.children[1].textContent !== nextSeconds || forceUpdate) {
        // secondsEl.classList.add("homepage-contest-countdown-digits--updating");

        if (forceUpdate) {
          secondsEl.classList.add(
            "homepage-contest-countdown-digits--updating"
          );
        }

        secondsEl.children[0].innerHTML = secondsEl.children[1].innerHTML;

        secondsEl.children[0].classList.toggle(
          "homepage-contest-countdown-digits-past",
          isPrevPast
        );

        secondsEl.children[1].innerHTML = nextSeconds;

        secondsEl.children[1].classList.toggle(
          "homepage-contest-countdown-digits-past",
          isPast
        );
      } else {
        secondsEl.classList.remove(
          "homepage-contest-countdown-digits--updating"
        );
      }

      if (this.forceAnimateCountdown) {
        this.forceAnimateCountdown = false;
      }

      requestAnimationFrame(updateCountdown);
    };

    requestAnimationFrame(updateCountdown);
  };

  onBodyResize = () => {
    this.tabControlList.style.setProperty("transition", "none");
    this.tabControlHighlightList.style.setProperty("transition", "none");
    this.updateTabControlListPosition();

    requestAnimationFrame(() => {
      this.tabControlList.style.removeProperty("transition");
      this.tabControlHighlightList.style.removeProperty("transition");
    });
  };
}
